import { useState, useEffect } from 'react';
import { FiArrowLeft, FiCheckCircle, FiClock, FiFileText, FiUser, FiImage, FiStar, FiEdit2, FiSave, FiX, FiMapPin } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { fetchTicketById, fetchUsers, updateTicket, getImageUrl, submitTicketFeedback } from '../api/api';
import './TicketDetails.css';

const getTicketImagePaths = (ticket) => {
  const paths = [];
  const seen = new Set(); // Prevent duplicates
  
  // Handle multiple image formats from backend (image_paths JSON array)
  if (ticket?.image_paths) {
    try {
      const parsed = typeof ticket.image_paths === 'string' ? 
        JSON.parse(ticket.image_paths) : ticket.image_paths;
      
      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          let path = '';
          if (typeof entry === 'string') path = entry.trim();
          else if (entry?.url) path = String(entry.url).trim();
          else if (entry?.path) path = String(entry.path).trim();
          else if (entry?.uri) path = String(entry.uri).trim();
          
          if (path && !seen.has(path)) {
            seen.add(path);
            paths.push(path);
          }
        });
      }
    } catch (error) {
      console.warn('[TicketDetails] Failed to parse image_paths:', error);
      // Handle comma-separated paths (fallback)
      const raw = String(ticket.image_paths || '');
      raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => {
          if (!seen.has(v)) {
            seen.add(v);
            paths.push(v);
          }
        });
    }
  }
  
  // Handle single image path (backward compatibility)
  const singlePath = ticket?.image_path || ticket?.imagePath;
  if (singlePath && singlePath.trim() && !seen.has(singlePath.trim())) {
    paths.push(singlePath.trim());
  }
  
  return paths;
};

const TicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const socket = useSocket();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState('');
  const [updatingAssignment, setUpdatingAssignment] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [assigningNearest, setAssigningNearest] = useState(false);

  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    setLoading(true);
    fetchTicketById(ticketId)
      .then((t) => { if (!cancelled) setTicket(t); })
      .catch(() => { if (!cancelled) setTicket(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticketId]);

  useEffect(() => {
    if (!socket || !user) return;
    const onCompleted = (t) => {
      if (String(t.id) === String(ticketId) && String(t.created_by) === String(user.id) && !ticket?.rating) {
        setShowFeedback(true);
        setTicket((prev) => prev ? { ...prev, ...t } : t);
      }
    };
    socket.on('ticket:completed', onCompleted);
    return () => socket.off('ticket:completed', onCompleted);
  }, [socket, user?.id, ticketId, ticket?.rating]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers('engineer')
        .then((data) => {
          // Filter engineers that have location data
          const engineersWithLocation = Array.isArray(data) ? 
            data.filter(eng => eng.latitude && eng.longitude) : [];
          setEngineers(engineersWithLocation);
        })
        .catch(() => setEngineers([]));
    }
  }, [user?.role]);

  useEffect(() => {
    if (ticket && (ticket.assigned_to || ticket.assignedTo)) {
      setSelectedEngineer(ticket.assigned_to || ticket.assignedTo);
    }
  }, [ticket]);

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (feedbackRating < 1 || feedbackRating > 5) return;
    setSubmittingFeedback(true);
    try {
      await submitTicketFeedback(ticketId, feedbackRating, feedbackComment);
      setTicket((prev) => ({ ...prev, rating: feedbackRating }));
      setShowFeedback(false);
      
      // Emit socket event to notify all connected clients
      if (socket) {
        const updatedTicket = { ...ticket, rating: feedbackRating, feedback_comment: feedbackComment };
        console.log('[WEB] Emitting ticket:updated from web app (feedback):', updatedTicket);
        socket.emit('ticket:updated', updatedTicket);
      }
    } catch (err) {
      alert(err.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleUpdateAssignment = async () => {
    if (!selectedEngineer) return;
    setUpdatingAssignment(true);
    try {
      const updatedTicket = await updateTicket(ticketId, { assigned_to: selectedEngineer });
      setTicket((prev) => ({ ...prev, ...updatedTicket }));
      setEditingAssignment(false);
    } catch (err) {
      alert(err.message || 'Failed to update assignment');
    } finally {
      setUpdatingAssignment(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedEngineer(ticket.assigned_to || ticket.assignedTo || '');
    setEditingAssignment(false);
  };

  const handleAssignNearestEngineer = async () => {
    if (!navigator.geolocation) {
      alert('Location is not supported by your browser');
      return;
    }
    
    setAssigningNearest(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Find nearest engineer based on location
          const nearestEngineer = findNearestEngineer(engineers, latitude, longitude);
          
          if (nearestEngineer) {
            const updatedTicket = await updateTicket(ticketId, { 
              assigned_to: nearestEngineer.id,
              assigned_location: { latitude, longitude }
            });
            setTicket((prev) => ({ ...prev, ...updatedTicket }));
            
            // Emit socket event
            if (socket) {
              console.log('[WEB] Emitting ticket:assigned (nearest engineer):', updatedTicket);
              socket.emit('ticket:assigned', updatedTicket);
            }
            
            alert(`Assigned to nearest engineer: ${nearestEngineer.name}`);
          } else {
            alert('No engineers available nearby');
          }
        } catch (err) {
          alert(err.message || 'Failed to assign nearest engineer');
        } finally {
          setAssigningNearest(false);
        }
      },
      (error) => {
        alert('Location access denied. Please enable location permissions.');
        setAssigningNearest(false);
      }
    );
  };

  const findNearestEngineer = (engineers, lat, lng) => {
    if (!engineers.length) return null;
    
    let nearestEngineer = null;
    let minDistance = Infinity;
    
    engineers.forEach(engineer => {
      if (engineer.latitude && engineer.longitude) {
        const distance = calculateDistance(lat, lng, engineer.latitude, engineer.longitude);
        if (distance < minDistance) {
          minDistance = distance;
          nearestEngineer = engineer;
        }
      }
    });
    
    return nearestEngineer;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  if (loading) {
    return (
      <div className="ticket-details">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
        <p>Loading...</p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-details">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
        <div className="not-found">Ticket not found.</div>
      </div>
    );
  }

  const status = (ticket.status || 'open').replace('-', ' ');
  const created = ticket.created_at || ticket.createdAt;
  const resolved = ticket.resolved_at || ticket.resolvedAt;

  const canChangeAssignment = user?.role === 'admin'; // Allow admins to change any ticket assignment

  
  return (
    <div className="ticket-details">
      <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Back</button>
      <div className="ticket-header">
        <h1>{ticket.title}</h1>
        <span className={`status-badge status-${status.toLowerCase().replace(' ', '-')}`}>{status}</span>
      </div>
      <div className="ticket-meta">
        <span><FiUser /> {ticket.created_by_name || 'Customer'}</span>
        <span><FiClock /> {created ? new Date(created).toLocaleString() : '—'}</span>
        <span><FiFileText /> Priority: {(ticket.priority || 'medium')}</span>
        {(ticket.assigned_to || ticket.assignedTo) ? (
          <span>
            <FiUser /> Assigned to: {ticket.assigned_to_name || 'Engineer'}
            {(canChangeAssignment) && (
              <button 
                className="edit-assignment-btn" 
                onClick={() => setEditingAssignment(true)}
                title="Change assignment"
              >
                <FiEdit2 />
              </button>
            )}
          </span>
        ) : (
          <span>
            <FiUser /> Not assigned
            {(canChangeAssignment) && (
              <button 
                className="edit-assignment-btn" 
                onClick={() => setEditingAssignment(true)}
                title="Assign engineer"
              >
                <FiEdit2 />
              </button>
            )}
          </span>
        )}
      </div>
      {editingAssignment && canChangeAssignment && (
        <div className="assignment-edit-section">
          <h3><FiEdit2 /> Change Engineer Assignment</h3>
          <div className="assignment-form">
            <select
              value={selectedEngineer}
              onChange={(e) => setSelectedEngineer(e.target.value)}
              className="engineer-select"
            >
              <option value="">Select an engineer...</option>
              {engineers.map((engineer) => (
                <option key={engineer.id} value={engineer.id}>
                  {engineer.name} ({engineer.email})
                </option>
              ))}
            </select>
            <div className="assignment-actions">
              <button
                onClick={handleUpdateAssignment}
                disabled={!selectedEngineer || updatingAssignment}
                className="save-assignment-btn"
              >
                <FiSave /> {updatingAssignment ? 'Updating...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={updatingAssignment}
                className="cancel-assignment-btn"
              >
                <FiX /> Cancel
              </button>
              <button
                onClick={handleAssignNearestEngineer}
                disabled={assigningNearest}
                className="nearest-engineer-btn"
                title="Assign nearest engineer based on location"
              >
                <FiMapPin /> {assigningNearest ? 'Finding...' : 'Assign Nearest'}
              </button>
            </div>
          </div>
        </div>
      )}
      {!editingAssignment && canChangeAssignment && (
        <div className="assignment-edit-section">
          <h3><FiEdit2 /> Engineer Assignment</h3>
          <div className="assignment-form">
            <select
              value={ticket.assigned_to || ticket.assignedTo || ''}
              onChange={async (e) => {
                const newEngineerId = e.target.value;
                if (newEngineerId && newEngineerId !== (ticket.assigned_to || ticket.assignedTo)) {
                  try {
                    const updatedTicket = await updateTicket(ticketId, { assigned_to: newEngineerId });
                    setTicket((prev) => ({ ...prev, ...updatedTicket }));
                    
                    // Emit socket event to notify all connected clients
                    if (socket) {
                      console.log('[WEB] Emitting ticket:assigned from web app:', updatedTicket);
                      socket.emit('ticket:assigned', updatedTicket);
                    }
                  } catch (err) {
                    alert(err.message || 'Failed to update assignment');
                  }
                }
              }}
              className="engineer-select"
            >
              <option value="">Unassigned</option>
              {engineers.map((engineer) => (
                <option key={engineer.id} value={engineer.id}>
                  {engineer.name} ({engineer.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="ticket-description">
        <h3>Description</h3>
        <p>{ticket.description || 'No description.'}</p>
      </div>
      {/* Images (if attached) */}
      {(() => {
        const imagePaths = getTicketImagePaths(ticket);
        if (imagePaths.length === 0) return null;
        return (
          <div className="ticket-image-section">
            <h3><FiImage /> Attached Images ({imagePaths.length})</h3>
            <div className="ticket-images-grid">
              {imagePaths.map((path, index) => (
                <img
                  key={path || index}
                  src={getImageUrl(path)}
                  alt={`Complaint attachment ${index + 1}`}
                  className="ticket-attachment-image"
                  onError={(e) => {
                    // Try to load with timestamp to bypass cache
                    const timestamp = new Date().getTime();
                    e.target.src = `${getImageUrl(path)}?t=${timestamp}`;
                  }}
                  crossOrigin="anonymous"
                />
              ))}
            </div>
          </div>
        );
      })()}
      {ticket.resolution && (
        <div className="ticket-resolution">
          <h3><FiCheckCircle /> Resolution</h3>
          <p>{ticket.resolution}</p>
          {resolved && <span className="comment-time">Resolved at {new Date(resolved).toLocaleString()}</span>}
        </div>
      )}
      {(ticket.status === 'resolved' || ticket.status === 'closed') && (
        <div className="ticket-feedback-section">
          {ticket.rating ? (
            <p><FiStar /> Your rating: {ticket.rating}/5</p>
          ) : (showFeedback || (String(ticket.created_by) === String(user?.id) && !ticket.rating)) && (
            <form onSubmit={handleSubmitFeedback} className="feedback-form">
              <h3><FiStar /> Rate this resolution</h3>
              <div className="feedback-stars">
                {[1, 2, 3, 4, 5].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`feedback-star ${feedbackRating >= r ? 'active' : ''}`}
                    onClick={() => setFeedbackRating(r)}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                placeholder="Optional comment..."
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                rows={2}
              />
              <button type="submit" disabled={submittingFeedback || feedbackRating < 1}>
                {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
